import React, { useCallback, useState } from 'react'
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Grid, Stack, TextField, Typography, InputAdornment, IconButton, Tooltip, Chip } from '@mui/material';
// icons
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from 'common/customIcons/search';
import EditIcon from "common/customIcons/edit";
import DeleteIcon from "common/customIcons/delete";
// utils and helpers
import { formattedDate, debounce } from "utilities/common";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../../redux/slices/modalSlice";
import { openSnackbar } from "../../../redux/slices/common";
import { deleteTemplateKit, getTemplateKits } from "../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import MDButton from 'components/MDButton';
import CommonAgGridTable from "components/tables/CommonAgGridTable";
import PlannerNotes from "common/CommonDrawer/hyperwolf/plannerNotes";
import { Transition } from "../distributionManagement/distributionDetails";
import { RemoveRedEyeRounded } from "@mui/icons-material";
import { BoxFilter } from "common/filters";
import { RegionFilter } from "common/filters";
import { getData } from 'utilities/common';
import { getPermissionsByMode } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';

const KitTemplatePage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch();

  const [refreshTable, setRefreshTable] = useState(false);
  const [customFilters, setCustomFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showPlannerNotes, setShowPlannerNotes] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState({});

  const columnDefs = [
    {
      headerName: 'Template ID',
      field: 'templateId',
      key: 'templateId',
      suppressMenu: false,
      minWidth: 150,
      flex: 1,
      sortable: false
    },
    {
      headerName: 'Region',
      field: 'region',
      key: 'region',
      suppressMenu: false,
      minWidth: 250,
      flex: 1.5,
      sortable: false,
      cellRenderer: (params) => {
        const { region } = params?.data?.region || {};
        if (!region) return "-";

        return (
          <Box arrow placement="bottom" className="w-100">
            <Typography variant='body2' fontWeight='semibold' color='#2E2E3A' style={{ whiteSpace: 'nowrap' }} m={0}>{region}</Typography>
          </Box>
        );
      },
    },
    {
      headerName: 'Sub Regions',
      field: 'subRegions',
      key: 'subRegions',
      suppressMenu: false,
      minWidth: 250,
      flex: 1.5,
      sortable: false,
      cellRenderer: ({ data }) => {
        if (!data) return "-";
        const firstSubRegionName = data?.subRegions?.[0]?.name;
        if (data?.subRegions?.length <= 1) return firstSubRegionName;

        return (
          <Tooltip
            title={
              <>
                {data?.subRegions?.map(subRegion => (
                  <Typography key={subRegion?.name} variant="caption" component="p" fontWeight={500} textAlign="left">
                    {subRegion?.name}
                  </Typography>
                ))}
              </>
            } arrow>
            <span className='table-row-chip chip-cus' style={{ cursor: "pointer" }}>
              {firstSubRegionName}
              <Chip
                key={data?._id || ""}
                label={`+${data?.subRegions?.length - 1}`}
                style={{ p: 0 }}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      headerName: 'Boxes',
      field: 'boxes',
      suppressMenu: false,
      sortable: false,
      minWidth: 200,
      flex: 1,
      cellStyle: { "white-space": "normal" },
      cellRenderer: ({ data }) => {
        const firstBoxName = data?.boxes?.[0]?.boxes?.name;
        if (data?.boxes?.length <= 1) return firstBoxName;

        return <Tooltip title={
          <>
            {data?.boxes?.map(box => (
              <Typography key={box?.boxes?.name} variant="caption" component="p" fontWeight={500} textAlign="left">
                {box?.boxes?.name}
              </Typography>
            ))}
          </>
        } arrow>
          <span className='table-row-chip chip-cus' style={{ cursor: "pointer" }}>
            {firstBoxName}
            <Chip
              key={data?._id || ""}
              label={`+${data?.boxes?.length - 1}`}
              style={{ p: 0 }}
            />
          </span>
        </Tooltip>
      }
    },
    {
      headerName: 'Min Products Per Region',
      field: 'minProductPerRegion',
      key: 'minProductPerRegion',
      suppressMenu: false,
      minWidth: 200,
      flex: 1,
      sortable: false,
    },
    {
      headerName: 'Max Products Per Region',
      field: 'maxProductPerRegion',
      key: 'maxProductPerRegion',
      suppressMenu: false,
      minWidth: 200,
      flex: 1,
      sortable: false,
    },
    {
      headerName: 'Creation Date',
      field: 'createdDate',
      key: 'createdDate',
      suppressMenu: false,
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueGetter: (params) => {
        return formattedDate(params?.data?.createdDate) || "-";
      }
    },
    {
      headerName: 'Planner Override Notes',
      field: 'plannerOverrideNotes',
      suppressMenu: false,
      minWidth: 200,
      flex: 1,
      sortable: false,
      cellRenderer: ({ data }) => {
        const hasNotes = data?.plannerOverrideNotes?.length
        if (!hasNotes) return "-";

        return <Box>
          <MDButton
            onClick={() => {
              setShowPlannerNotes(true)
              setSelectedTemplate(data)
            }}
            size='small'
            startIcon={<RemoveRedEyeRounded fontSize='small' />} variant='contained' color='primary' className='btn-primary-light'>
            View Notes
          </MDButton>
        </Box>
      }
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: (props) => {
        return <Box display='inline-flex'>
          <IconButton  disabled={!canView ? true : false} onClick={() => navigate(`/hyperwolf/kit-template/view-template/${props?.data._id}`)} color='primary' size='small' variant='primary'>
            <VisibilityIcon width='16px' height='16px' />
          </IconButton>
          <IconButton
            disabled={!canEdit}
            sx={{ marginInline: 1 }}
            color='primary' size='small' variant='primary'
            onClick={() => {
              navigate(`/hyperwolf/kit-template/update-template/${props?.data._id}`)
            }}
          >
            <EditIcon width='16px' height='16px' />
          </IconButton>
          <IconButton
            color='error'
            size='small'
             disabled={!canDelete}
            variant='primary'
            onClick={() => dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: props.data?._id }, modal: 'DELETE_MODAL' }))}
          >
            <DeleteIcon width='16px' height='16px' />
          </IconButton>
        </Box>;
      },
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      maxWidth: 150,
      pinned: 'right'
    },
  ];

  const handleChange = (e) => {
    const { value, name } = e.target;
    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(!refreshTable);
  };

  const deleteHandler = async (id) => {
    try {
      dispatch(setModalLoader(true));

      const res = await dispatch(deleteTemplateKit(id)).unwrap();
      setRefreshTable(prev => !prev)

      dispatch(openSnackbar({ content: res?.message, color: 'success', icon: 'check' }));
      dispatch(closeCustomModalDialog());
    } catch (err) {
      dispatch(setModalLoader(false));
      dispatch(openSnackbar({ content: err?.message, color: 'error', icon: 'warning' }));
    };
  };

  const searchHandler = useCallback(debounce((event) => {
    setCustomFilters(prev => ({ ...prev, search: event.target.value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleClearFilters = () => {
    setCustomFilters({ search: customFilters.search || "" })
    setRefreshTable(prev => !prev)
  }

   let localUser = getData("login-user-info");
    if (localUser) {
      localUser = JSON.parse(localUser);
    }
  
    const adminMode = localStorage.getItem("adminsMode")
      ? JSON.parse(localStorage.getItem("adminsMode"))
      : null;
    const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);
  
    const canCreate = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Create");
    const canView = hasSectionPermission(permissionsForMode, "Inventory Distribution", "View Only");
    const canDelete = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Delete");
    const canEdit = hasSectionPermission(permissionsForMode, "Inventory Distribution", "Edit/Update");

  return (
    <>
    {
      canView &&
      <>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} className="ag-grid-table-white">
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4' fontWeight="semibold">Kit Templates</Typography>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
              {/* Search filter */}
              <TextField
                className="custom-search-field"
                placeholder="Search"
                value={searchTerm || ""}
                onChange={e => {
                  const { value } = e.target;
                  setSearchTerm(value);
                  searchHandler(e);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    <IconButton size='small'>
                      <SearchIcon width='18px' height='18px' />
                    </IconButton>
                  </InputAdornment>
                }}
                style={{ minWidth: "300px" }}
              />
              {
                canCreate &&
                <Link to="/hyperwolf/kit-template/create-template">
                  <MDButton
                    style={{ height: "100%", minWidth: "160px" }}
                    startIcon={<AddRoundedIcon fontSize="large" />}
                    variant="contained"
                    color="primary"
                  >
                    Create Template
                  </MDButton>
                </Link>
              }
            </Stack>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box className='cus-filter-sec' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} lg={3} xxxl={2} className='p-remove'>
              <BoxFilter
                value={customFilters?.boxId}
                handleChange={handleChange}
                placeholder={"Box Type"}
                selectAllLabel={"All Boxes"}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <RegionFilter
                value={customFilters?.regionId || ""}
                handleChange={handleChange}
                placeholder={"Region"}
                selectAllLabel={"All Regions"}
              />
            </Grid>
            <Grid item xs={12} lg={3} xxxl={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                disabled={!Object.values(customFilters || {})?.filter(Boolean)?.length}
                onClick={handleClearFilters}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        <div
          className="ag-theme-quartz table-nodata-size"
          style={{ height: 550, width: "100%" }}
        >
          <CommonAgGridTable
            pagination={false}
            columnDefs={columnDefs}
            getDataMethod={getTemplateKits}
            refreshTable={refreshTable}
            customFilters={customFilters}
            onCellClicked={(props) => {
              // Prevent row click if click originated from the actions column
           
              if (props?.column && (["plannerOverrideNotes", "actions"].includes(props.column.getColId()))) {
                return;
              }
              navigate(`/hyperwolf/kit-template/view-template/${props?.data._id}`)
            }}
          />
        </div>
      </Box>

      {/* Planner override notes */}
      <PlannerNotes
        open={showPlannerNotes}
        TransitionComponent={Transition}
        handleClose={() => setShowPlannerNotes(false)}
        templateData={selectedTemplate}
      />
      </>
    }
    </>
  )
}

export default KitTemplatePage
